import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  getApiPath,
  getLocale,
  getPrivacyLink,
  getShowPrivacy,
  getThankYouLink,
} from "../../utils/apiSettings";
import { formatCurrency, isValidEmail } from "../../utils/utils";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Upload } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";

const locale = getLocale() ?? "en-US";
const countryCode = locale.split("-")[1]?.toLowerCase() ?? "us";
const localeCode = locale.split("-")[0]?.toLowerCase() ?? "en";
const showPrivacy = getShowPrivacy();
const minQty = 50; // Minimum quantity allowed
const maxFiles = 4; // Maximum number of files allowed per side
const inputDefaults = {
  company: "",
  name: "",
  email: "",
  phone: "",
  qty: "",
  finish: "",
  size: "",
  color: "",
  mold: "",
  edge: "",
  packaging: "",
  packagingQty: "",
  message: "",
  frontFiles: [],
  backFiles: [],
  accepted: false,
  captcha: null,
};
const priceDefaults = {
  coins_total_price: null,
  coins_unit_price: null,
  packaging_total_price: null,
};

export function ContactForm() {
  const { t } = useTranslation();

  const finishOptions = [
    { value: "Polished Gold", label: t("finishes.polishedGold") },
    { value: "Polished Silver", label: t("finishes.polishedSilver") },
    { value: "Polished Copper", label: t("finishes.polishedCopper") },
    { value: "Antique Gold", label: t("finishes.antiqueGold") },
    { value: "Antique Silver", label: t("finishes.antiqueSilver") },
    { value: "Antique Copper", label: t("finishes.antiqueCopper") },
  ];
  const sizeOptions = [
    { value: "1.5 Inches", label: t("sizes.1_5Inch") },
    { value: "1.75 Inches", label: t("sizes.1_75Inch") },
    { value: "2 Inches", label: t("sizes.2Inch") },
    { value: "2.25 Inches", label: t("sizes.2_25Inch") },
    { value: "2.5 Inches", label: t("sizes.2_5Inch") },
    { value: "2.75 Inches", label: t("sizes.2_75Inch") },
    { value: "3 Inches", label: t("sizes.3Inch") },
    { value: "3.25 Inches", label: t("sizes.3_25Inch") },
    { value: "3.5 Inches", label: t("sizes.3_5Inch") },
    { value: "3.75 Inches", label: t("sizes.3_75Inch") },
    { value: "4 Inches", label: t("sizes.4Inch") },
  ];
  const colorOptions = [
    { value: "No Color", label: t("colors.noColor") },
    { value: "One Side", label: t("colors.oneSide") },
    { value: "Two Sides", label: t("colors.twoSide") },
  ];
  const moldOptions = [
    { value: "Design on one side", label: t("molds.one") },
    { value: "Same design on both sides", label: t("molds.same") },
    { value: "Each side different design", label: t("molds.two") },
  ];
  const edgeOptions = [
    { value: "Standard", label: t("edges.standard") },
    { value: "Rope", label: t("edges.rope") },
    { value: "Spur", label: t("edges.spur") },
    { value: "Wave", label: t("edges.wave") },
    { value: "Spiral", label: t("edges.spiral") },
    { value: "Cross Cut", label: t("edges.crossCut") },
  ];
  const packagingOptions = [
    {
      value: "Plastic sleeve",
      label: t("packagings.plasticSleeve"),
      stock: true,
      sizes: [
        "1.5 Inches",
        "1.75 Inches",
        "2 Inches",
        "2.25 Inches",
        "2.5 Inches",
        "2.75 Inches",
        "3 Inches",
        "3.25 Inches",
        "3.5 Inches",
        "3.75 Inches",
        "4 Inches",
      ],
    },
    {
      value: "Plastic case",
      label: t("packagings.plasticCase"),
      stock: false,
      sizes: ["1.5 Inches", "1.75 Inches", "2 Inches"],
    },
    {
      value: "Frame",
      label: t("packagings.frame"),
      stock: false,
      sizes: [
        "1.5 Inches",
        "1.75 Inches",
        "2 Inches",
        "2.25 Inches",
        "2.5 Inches",
        "2.75 Inches",
        "3 Inches",
        "3.25 Inches",
        "3.5 Inches",
      ],
    },
    {
      value: "Wooden case",
      label: t("packagings.woodenCase"),
      stock: false,
      sizes: ["1.5 Inches", "1.75 Inches", "2 Inches"],
    },
  ];

  const [formData, setFormData] = useState(inputDefaults);
  const [formStatus, setFormStatus] = useState("");
  const [priceData, setPriceData] = useState(priceDefaults);
  const [coinsPriceStatus, setCoinsPriceStatus] = useState("");
  const [packPriceStatus, setPackPriceStatus] = useState("");
  const [submitTrigger, setSubmitTrigger] = useState(0);
  const textareaRef = useRef(null);
  const frontFileRef = useRef(null);
  const backFileRef = useRef(null);
  const recaptchaRef = useRef(null);

  const handleChange = (e, side) => {
    const { name, value, type, checked, files } = e.target;

    if (files) {
      if (side === "front") {
        setFormData((prevData) => ({
          ...prevData,
          frontFiles: [...prevData.frontFiles, e.target.files[0]],
        }));
      } else if (side === "back") {
        setFormData((prevData) => ({
          ...prevData,
          backFiles: [...prevData.backFiles, e.target.files[0]],
        }));
      }
    } else {
      if (name === "packaging" && value === "Plastic sleeve") {
        // If packaging is Plastic sleeve, set packagingQty = qty
        setFormData((prevData) => ({
          ...prevData,
          packagingQty: prevData.qty,
          [name]: value,
        }));
      } else if (name === "qty" && formData.packaging === "Plastic sleeve") {
        setFormData((prevData) => ({
          ...prevData,
          packagingQty: value,
          [name]: value,
        }));
      } else {
        setFormData((prevData) => ({
          ...prevData,
          [name]: type === "checkbox" ? checked : value,
        }));
      }
    }
  };

  const handleTextareaHeight = (e) => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = textarea.scrollHeight + "px";
    }
  };

  const handleMinQty = (e) => {
    const { value } = e.target;

    if (value) {
      let newValue = parseInt(value);
      newValue = isNaN(newValue) ? 0 : newValue;
      if (newValue < minQty) {
        newValue = minQty;
      }

      if (formData.packaging === "Plastic sleeve") {
        // If packaging is Plastic sleeve, set packagingQty = qty
        setFormData((prevData) => ({
          ...prevData,
          packagingQty: newValue.toString(),
          qty: newValue.toString(),
        }));
      } else {
        setFormData((prevData) => ({
          ...prevData,
          qty: newValue.toString(),
        }));
      }
    }
  };

  const handleFileRemove = (index, side) => {
    if (side === "front") {
      setFormData((prevData) => ({
        ...prevData,
        frontFiles: [...prevData.frontFiles.filter((file, i) => i !== index)],
      }));

      // Reset file input
      if (frontFileRef.current) {
        frontFileRef.current.value = "";
      }
    } else if (side === "back") {
      setFormData((prevData) => ({
        ...prevData,
        backFiles: [...prevData.backFiles.filter((file, i) => i !== index)],
      }));

      // Reset file input
      if (backFileRef.current) {
        backFileRef.current.value = "";
      }
    }
  };

  const handleFileUpload = (side) => {
    if (side === "front") {
      frontFileRef.current.click();
    } else if (side === "back") {
      backFileRef.current.click();
    }
  };

  const scrollToError = () => {
    // scroll to the first invalid field
    const firstInvalidField = document.querySelector(
      "#challenge24-contact-form .error-anchor"
    );
    if (firstInvalidField) {
      const elementRect = firstInvalidField.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.scrollY;
      const middle = absoluteElementTop - window.innerHeight / 2;
      window.scrollTo({ top: middle, behavior: "smooth" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formStatus === "sending") {
      return;
    }

    setSubmitTrigger((prev) => prev + 1);

    if (
      !formData.name ||
      !isValidEmail(formData.email) ||
      (showPrivacy && !formData.accepted)
    ) {
      setFormStatus("invalid");
      return;
    }

    setFormStatus("sending");

    // Validate Recaptcha
    if (!formData.captcha) {
      try {
        const token = await recaptchaRef.current?.executeAsync();

        if (!token) {
          setFormStatus("recaptcha_error");
          return;
        }
      } catch (e) {
        setFormStatus("recaptcha_error");
        return;
      }
    }

    // Handle form submission logic here
    try {
      const zc_gad = document.getElementById("zc_gad")?.["value"] ?? "";

      const data = new FormData();
      data.append("company", formData.company);
      data.append("name", formData.name);
      data.append("email", formData.email);
      data.append("phone", formData.phone);
      data.append("qty", formData.qty);
      data.append("finish", formData.finish);
      data.append("size", formData.size);
      data.append("color", formData.color);
      data.append("mold", formData.mold);
      data.append("edge", formData.edge);
      data.append("message", formData.message);
      data.append("coins_price", priceData.coins_total_price);
      data.append("packaging", formData.packaging);
      data.append("packaging_qty", formData.packagingQty);
      data.append("packaging_price", priceData.packaging_total_price);
      data.append("pageUrl", window.location.href);
      data.append("zc_gad", zc_gad);

      [...formData.frontFiles].forEach((file) =>
        data.append("frontFiles[]", file)
      );

      [...formData.backFiles].forEach((file) =>
        data.append("backFiles[]", file)
      );

      const request = await axios.post(getApiPath() + "/contact", data);
      const result = request.data;

      if (result?.success) {
        const redirectLink = getThankYouLink() ?? null;
        if (redirectLink) {
          window.location.href = redirectLink;
        } else {
          setFormStatus("sent");

          // Reset inputs
          setFormData((prevData) => ({
            ...inputDefaults,
            company: prevData.company, // Keep company field
            name: prevData.name, // Keep name field
            email: prevData.email, // Keep email field
            phone: prevData.phone, // Keep phone field
          }));

          // Reset file inputs
          if (frontFileRef.current) {
            frontFileRef.current.value = "";
          }
          if (backFileRef.current) {
            backFileRef.current.value = "";
          }
        }
      } else {
        setFormStatus("error");
      }
    } catch (e) {
      setFormStatus("error");
      return;
    }
  };

  // Scroll to error if form is invalid after submission
  useEffect(() => {
    if (formStatus === "invalid") {
      scrollToError();
    }
  }, [submitTrigger, formStatus]);

  // Fetch coins price based on form data
  useEffect(() => {
    const qty = parseInt(formData.qty);
    if (
      isNaN(qty) ||
      qty < minQty ||
      !formData.size ||
      !formData.color ||
      !formData.mold ||
      !formData.edge ||
      !formData.finish
    ) {
      setPriceData((prevData) => ({
        ...prevData,
        coins_total_price: null,
        coins_unit_price: null,
      }));
      return;
    }

    const fetchPrice = async () => {
      setCoinsPriceStatus("fetching");

      try {
        const request = await axios.get(getApiPath() + "/price", {
          params: {
            qty: formData.qty,
            size: formData.size,
            color: formData.color,
            mold: formData.mold,
            edge: formData.edge,
            finish: formData.finish,
          },
        });
        const result = request.data;

        if (result?.coins) {
          setPriceData((prevData) => ({
            ...prevData,
            coins_total_price: result.coins?.total_price,
            coins_unit_price: result.coins?.unit_price,
          }));
          setCoinsPriceStatus("");
        } else {
          setCoinsPriceStatus("error");
        }
      } catch (e) {
        setCoinsPriceStatus("error");
        return;
      }
    };

    fetchPrice();
  }, [
    formData.qty,
    formData.size,
    formData.color,
    formData.mold,
    formData.edge,
    formData.finish,
  ]);

  // Fetch packaging price based on form data
  useEffect(() => {
    const qty = parseInt(formData.packagingQty);
    if (isNaN(qty) || qty < 0 || !formData.packaging) {
      setPriceData((prevData) => ({
        ...prevData,
        packaging_total_price: null,
      }));
      return;
    }

    const fetchPrice = async () => {
      setPackPriceStatus("fetching");

      try {
        const request = await axios.get(getApiPath() + "/price", {
          params: {
            packaging: formData.packaging,
            packaging_qty: formData.packagingQty,
          },
        });
        const result = request.data;

        if (result?.packaging) {
          setPriceData((prevData) => ({
            ...prevData,
            packaging_total_price: result.packaging?.total_price,
          }));
          setPackPriceStatus("");
        } else {
          setPackPriceStatus("error");
        }
      } catch (e) {
        setPackPriceStatus("error");
        return;
      }
    };

    fetchPrice();
  }, [formData.packaging, formData.packagingQty]);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="max-w-[660px] mx-auto">
        <div className="flex flex-wrap gap-x-5">
          {/* Company field */}
          <div className="flex-1 basis-xs mb-8">
            <label htmlFor="contact-company" className="form-label">
              {t("companyName")}
            </label>
            <input
              id="contact-company"
              type="text"
              name="company"
              className="form-input"
              required
              value={formData.company}
              onChange={handleChange}
            />
          </div>
          {/* Name field */}
          <div className="flex-1 basis-xs mb-4">
            <label htmlFor="contact-name" className="form-label">
              {t("name")}
              <span className="text-red-500">*</span>
            </label>
            <input
              id="contact-name"
              type="text"
              name="name"
              className={`${
                formStatus === "invalid" && !formData.name
                  ? "border-red-500 error-anchor"
                  : ""
              } form-input`}
              required
              value={formData.name}
              onChange={handleChange}
            />
            <div
              className={`${
                formStatus === "invalid" && !formData.name
                  ? ""
                  : "opacity-0 invisible"
              } form-error-msg`}
            >
              {t("invalidName")}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-5">
          {/* Email field */}
          <div className="flex-1 basis-xs mb-4">
            <label htmlFor="contact-email" className="form-label">
              {t("email")}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="contact-email"
              name="email"
              className={`${
                formStatus === "invalid" && !isValidEmail(formData.email)
                  ? "border-red-500 error-anchor"
                  : ""
              } form-input`}
              required
              value={formData.email}
              onChange={handleChange}
            />
            <div
              className={`${
                formStatus === "invalid" && !isValidEmail(formData.email)
                  ? ""
                  : "opacity-0 invisible"
              } form-error-msg`}
            >
              {t("invalidEmail")}
            </div>
          </div>
          {/* Phone field */}
          <div className="flex-1 basis-xs mb-8">
            <label htmlFor="contact-phone" className="form-label">
              {t("phone")}
            </label>
            <PhoneInput
              inputProps={{ id: "contact-phone" }}
              inputClass="form-phone-input"
              country={countryCode}
              value={formData.phone}
              onChange={(value) =>
                setFormData({ ...formData, phone: value ? "+" + value : "" })
              }
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-x-5">
          {/* Quantity field */}
          <div className="flex-1 basis-xs mb-8">
            <label htmlFor="contact-qty" className="form-label">
              {t("quantity")}
              <span className="ml-1 text-sm text-foghorn">
                ({t("minQtyInfo", { minQty })})
              </span>
            </label>
            <input
              type="number"
              id="contact-qty"
              name="qty"
              className="form-input"
              max={1000000}
              value={formData.qty}
              onChange={handleChange}
              onKeyDown={(e) =>
                ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()
              }
              onBlur={handleMinQty}
            />
          </div>
          {/* Finish field */}
          <div className="flex-1 basis-xs mb-8">
            <label htmlFor="contact-finish" className="form-label">
              {t("finish")}
            </label>
            <select
              id="contact-finish"
              name="finish"
              className="form-select"
              value={formData.finish}
              onChange={handleChange}
              required
            >
              <option value="" hidden disabled>
                {t("selectFinish")}
              </option>
              {finishOptions.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-5">
          {/* Size field */}
          <div className="flex-1 basis-xs mb-8">
            <label htmlFor="contact-size" className="form-label">
              {t("size")}
            </label>
            <select
              id="contact-size"
              name="size"
              className="form-select"
              value={formData.size}
              onChange={handleChange}
              required
            >
              <option value="" hidden disabled>
                {t("selectSize")}
              </option>
              {sizeOptions.map((option, index) => (
                <option
                  key={index}
                  value={option.value}
                  disabled={
                    formData.packaging &&
                    !packagingOptions
                      .find((po) => po.value === formData.packaging)
                      .sizes.includes(option.value)
                  }
                >
                  {formData.packaging &&
                  !packagingOptions
                    .find((po) => po.value === formData.packaging)
                    .sizes.includes(option.value)
                    ? `${option.label} (${t("sizePackUnavail")})`
                    : option.label}
                </option>
              ))}
            </select>
          </div>
          {/* Color field */}
          <div className="flex-1 basis-xs mb-8">
            <label htmlFor="contact-color" className="form-label">
              {t("color")}
            </label>
            <select
              id="contact-color"
              name="color"
              className="form-select"
              value={formData.color}
              onChange={handleChange}
              required
            >
              <option value="" hidden disabled>
                {t("selectColor")}
              </option>
              {colorOptions.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-5">
          {/* Mold field */}
          <div className="flex-1 basis-xs mb-8">
            <label htmlFor="contact-mold" className="form-label">
              {t("mold")}
            </label>
            <select
              id="contact-mold"
              name="mold"
              className="form-select"
              value={formData.mold}
              onChange={handleChange}
              required
            >
              <option value="" hidden disabled>
                {t("selectMold")}
              </option>
              {moldOptions.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {/* Edge field */}
          <div className="flex-1 basis-xs mb-8">
            <label htmlFor="contact-edge" className="form-label">
              {t("edge")}
            </label>
            <select
              id="contact-edge"
              name="edge"
              className="form-select"
              value={formData.edge}
              onChange={handleChange}
              required
            >
              <option value="" hidden disabled>
                {t("selectEdge")}
              </option>
              {edgeOptions.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-5">
          {/* Packaging field */}
          <div className="flex-1 basis-xs mb-8">
            <label htmlFor="contact-packaging" className="form-label">
              {t("packaging")}
            </label>
            <select
              id="contact-packaging"
              name="packaging"
              className="form-select"
              value={formData.packaging}
              onChange={handleChange}
              required
            >
              <option value="" hidden disabled>
                {t("selectPackaging")}
              </option>
              {packagingOptions.map((option, index) => (
                <option
                  key={index}
                  value={option.value}
                  disabled={
                    (formData.size && !option.sizes.includes(formData.size)) ||
                    Number(option.stock) < 1
                  }
                >
                  {formData.size && !option.sizes.includes(formData.size)
                    ? `${option.label} (${t("packSizeUnavail")})`
                    : Number(option.stock) > 0
                    ? option.label
                    : `${option.label} (${t("outOfStock")})`}
                </option>
              ))}
            </select>
          </div>
          {/* PackagingQty field */}
          <div className="flex-1 basis-xs mb-8">
            <label htmlFor="contact-packaging-qty" className="form-label">
              {t("packagingQty")}
            </label>
            <input
              type="number"
              id="contact-packaging-qty"
              name="packagingQty"
              className="form-input"
              min={0}
              max={1000000}
              value={formData.packagingQty}
              readOnly={formData.packaging === "Plastic sleeve"}
              onChange={handleChange}
              onKeyDown={(e) =>
                ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()
              }
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-x-5">
          <div className="flex-1 basis-xs"></div>
          {/* Price info */}
          <div className="flex-1 basis-xs mb-8 px-3 py-4 bg-[#fef6e2]">
            <div className="mb-2">
              <div className="text-xl">{t("coinsPrice")}</div>
              {priceData.coins_total_price === null &&
              coinsPriceStatus !== "error" &&
              coinsPriceStatus !== "fetching" ? (
                <div className="text-foghorn">{t("coinsPriceInfo")}</div>
              ) : (
                <PriceInfo
                  status={coinsPriceStatus}
                  price={priceData.coins_total_price}
                />
              )}
            </div>
            <div>
              <div className="text-xl">{t("packPrice")}</div>
              {priceData.packaging_total_price === null &&
              packPriceStatus !== "error" &&
              packPriceStatus !== "fetching" ? (
                <div className="text-foghorn">{t("packPriceInfo")}</div>
              ) : (
                <PriceInfo
                  status={packPriceStatus}
                  price={priceData.packaging_total_price}
                />
              )}
            </div>
          </div>
        </div>
        <div className="mb-8">
          {/* Message field */}
          <label htmlFor="contact-message" className="form-label">
            {t("message")}
          </label>
          <textarea
            ref={textareaRef}
            name="message"
            id="contact-message"
            className="form-textarea"
            style={{ overflow: "hidden" }}
            value={formData.message}
            onInput={handleTextareaHeight}
            onChange={handleChange}
          />
        </div>
        {/* Attachments */}
        <div className="mb-8">
          <div className="mb-4 text-xl">{t("attachLabel")}</div>
          <div>{t("attachInfo")}</div>
          <div className="mb-4 text-sm text-foghorn">
            {t("attachLimit", { maxFiles, count: maxFiles })}
          </div>
          <div className="flex flex-wrap gap-5">
            <div className="flex-1 basis-xs min-w-0 sm:min-w-80">
              <div className="text-lg">{t("frontLabel")}</div>
              {formData.frontFiles.length > 0 && (
                <div>
                  {formData.frontFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-1 mb-1">
                      <span className="text-xs whitespace-nowrap overflow-x-hidden overflow-ellipsis">
                        {file?.name}
                      </span>
                      <span
                        className="p-0 text-xl leading-none border-none bg-transparent cursor-pointer"
                        onClick={() => handleFileRemove(index, "front")}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {formData.frontFiles.length < maxFiles && (
                <button
                  type="button"
                  className="form-btn"
                  onClick={() => handleFileUpload("front")}
                >
                  <Upload size={20} />
                  {t("uploadLabel")}
                </button>
              )}
              <input
                type="file"
                ref={frontFileRef}
                accept=".jpg,.jpeg,.png,.svg,.pdf"
                className="hidden"
                onChange={(e) => handleChange(e, "front")}
              />
            </div>
            <div className="flex-1 basis-xs min-w-0 sm:min-w-80">
              <div className="text-lg">{t("backLabel")}</div>
              {formData.backFiles.length > 0 && (
                <div>
                  {formData.backFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-1 mb-1">
                      <span className="text-xs whitespace-nowrap overflow-x-hidden overflow-ellipsis">
                        {file?.name}
                      </span>
                      <span
                        className="p-0 text-xl leading-none border-none bg-transparent cursor-pointer"
                        onClick={() => handleFileRemove(index, "back")}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {formData.backFiles.length < maxFiles && (
                <button
                  type="button"
                  className="form-btn"
                  onClick={() => handleFileUpload("back")}
                >
                  <Upload size={20} />
                  {t("uploadLabel")}
                </button>
              )}
              <input
                type="file"
                ref={backFileRef}
                accept=".jpg,.jpeg,.png,.svg,.pdf"
                className="hidden"
                onChange={(e) => handleChange(e, "back")}
              />
            </div>
          </div>
        </div>
        {/* Info */}
        {showPrivacy && (
          <div className="mb-4">
            <div>
              <label
                htmlFor="contact-privacy"
                className="mb-1 flex items-center gap-2"
              >
                <input
                  type="checkbox"
                  id="contact-privacy"
                  name="accepted"
                  className={`${
                    formStatus === "invalid" && !formData.accepted
                      ? "border-red-500 error-anchor"
                      : ""
                  } form-checkbox`}
                  checked={formData.accepted}
                  onChange={handleChange}
                />
                <span>
                  {t("privacyAccept")}{" "}
                  <a
                    href={getPrivacyLink() ?? "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("privacyPolicy")}
                  </a>
                </span>
              </label>
              <div
                className={`${
                  formStatus === "invalid" && !formData.accepted
                    ? ""
                    : "opacity-0 invisible"
                } form-error-msg`}
              >
                {t("invalidPrivacy")}
              </div>
            </div>
          </div>
        )}
        {/* Response */}
        <div className="min-h-5">
          {formStatus === "sent" && (
            <span className="text-green-600">{t("statusSent")}</span>
          )}
          {formStatus === "error" && (
            <span className="text-red-500">{t("statusError")}</span>
          )}
        </div>
        {/* Submit button */}
        <div className="mb-4">
          <button
            type="submit"
            className="form-btn-lg"
            disabled={formStatus === "sending"}
          >
            {formStatus === "sending" && (
              <span className="loading loading-spinner"></span>
            )}
            {t("submit")}
          </button>
        </div>
        {/* Recaptcha */}
        <div>
          <ReCAPTCHA
            sitekey={import.meta.env.VITE_RECAPTCHA_KEY}
            size="invisible"
            badge="inline"
            hl={localeCode}
            ref={recaptchaRef}
            onChange={(token) => {
              setFormData({ ...formData, captcha: token });
            }}
          />
          <div
            className={`${
              formStatus === "recaptcha_error" ? "" : "opacity-0 invisible"
            } form-error-msg`}
          >
            {t("invalidCaptcha")}
          </div>
        </div>
      </div>
    </form>
  );
}

const PriceInfo = ({ status, price }) => {
  const { t } = useTranslation();

  if (status === "error") {
    return <div className="h-7 text-red-500">{t("priceError")}</div>;
  } else if (status === "fetching") {
    return (
      <div className="h-7 flex flex-wrap items-baseline gap-2">
        <span>{t("estimated")}:</span>
        <span className="loading loading-bars loading-xs h-5 relative top-1 text-subnautical"></span>
      </div>
    );
  } else if (price !== null) {
    return (
      <div className="h-7 flex flex-wrap items-baseline gap-2">
        <span>{t("estimated")}:</span>
        <span className="text-lg">{formatCurrency(price)}</span>
        <small className="text-foghorn">({t("taxExcl")})</small>
      </div>
    );
  } else {
    return null;
  }
};
